import type { MyBookingListItem } from '@massilia/queries'

import { Button } from '@massilia/ui'
import { Link } from '@tanstack/react-router'
import { format, isValid, parseISO } from 'date-fns'
import { fr } from 'date-fns/locale'
import { Plane, Users } from 'lucide-react'

import { BookingStatusBadge } from './booking-status-badge'

interface BookingListItemProps {
  booking: MyBookingListItem
}

export function BookingListItem({ booking }: BookingListItemProps) {
  const outboundSegment = booking.flightSegments[0]
  const returnSegment =
    booking.flightSegments.length > 1
      ? booking.flightSegments[booking.flightSegments.length - 1]
      : null

  return (
    <div className="border-gray-light-200 flex flex-col overflow-hidden rounded-2xl border md:flex-row md:items-center md:justify-between">
      {/* Top/Left: image + details */}
      <div className="flex items-center gap-3 md:gap-6">
        <img
          alt=""
          className="h-24 w-24 shrink-0 object-cover md:h-37.5 md:w-37.5"
          src="/images/empty-state-airplane.jpg"
        />
        <div className="flex flex-col gap-1.5 py-3 pr-3 md:gap-3 md:py-4 md:pr-0">
          <div className="flex flex-wrap items-center gap-1.5 md:gap-3">
            <h3 className="text-gray-light-900 text-sm font-semibold md:text-lg">
              {booking.route}
            </h3>
            <BookingStatusBadge status={booking.status} />
          </div>
          <div className="flex flex-col gap-1 md:gap-2">
            {/* Outbound flight */}
            {outboundSegment && (
              <div className="flex items-center gap-2 md:gap-4">
                <Plane className="text-gray-light-600 size-3.5 shrink-0 md:size-5" />
                <span className="text-gray-light-900 text-xs font-semibold md:text-sm">
                  {formatFlightDateTime(outboundSegment.departureAt)}
                </span>
              </div>
            )}

            {/* Return flight */}
            {returnSegment && (
              <div className="flex items-center gap-2 md:gap-4">
                <Plane className="text-gray-light-600 size-3.5 shrink-0 rotate-180 md:size-5" />
                <span className="text-gray-light-900 text-xs font-semibold md:text-sm">
                  {formatFlightDateTime(returnSegment.departureAt)}
                </span>
              </div>
            )}

            {/* Passenger count */}
            <div className="flex items-center gap-2 md:gap-4">
              <Users className="text-gray-light-600 size-3.5 shrink-0 md:size-5" />
              <span className="text-gray-light-900 text-xs font-semibold md:text-sm">
                {booking.passengerCount} voyageur{booking.passengerCount > 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom/Right: price + CTA button */}
      <div className="flex items-center justify-between gap-3 border-t px-4 py-2.5 md:shrink-0 md:flex-col md:items-end md:justify-end md:self-stretch md:border-t-0 md:px-0 md:py-6 md:pr-6">
        <p className="text-gray-light-900 text-sm font-semibold md:mb-3 md:text-lg">
          {formatPrice(booking.totalPrice, booking.currency)}
        </p>
        <Link params={{ bookingRef: booking.reference }} to="/mes-reservations/$bookingRef">
          <Button className="bg-brand-600 hover:bg-brand-700 text-xs font-medium whitespace-nowrap text-white md:text-sm">
            Consulter la réservation
          </Button>
        </Link>
      </div>
    </div>
  )
}

function formatFlightDateTime(isoDate: string): string {
  const date = parseISO(isoDate)
  if (!isValid(date)) return isoDate
  const formatted = format(date, "d MMMM yyyy : H'h'mm", { locale: fr })
  return formatted.charAt(0).toUpperCase() + formatted.slice(1)
}

function formatPrice(cents: number, currency = 'EUR'): string {
  return new Intl.NumberFormat('fr-FR', {
    currency,
    minimumFractionDigits: 2,
    style: 'currency',
  }).format(cents / 100)
}
